import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Award, Languages, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";

const initials = (n = "") => n.replace(/[[\]]/g, "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "A";

export function AstrologerCards() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/content/astrologers").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list.length) return null;

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-16" data-testid="astrologer-cards">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-400/90">Meet Our Astrologers</p>
          <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-slate-50">Guidance from people who know the stars</h3>
        </div>
      </div>
      <div className="flex sm:grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3 overflow-x-auto sm:overflow-visible snap-x snap-mandatory no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
        {list.map((a, i) => (
          <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }} className="group mc-card p-6 flex flex-col min-w-[84%] sm:min-w-0 snap-center" data-testid={`astrologer-card-${i}`}>
            <div className="flex items-center gap-4">
              <div className="relative h-16 w-16 shrink-0 rounded-full p-[2px] bg-gradient-to-br from-amber-300 via-amber-500/40 to-purple-500/60 transition-transform duration-300 group-hover:scale-105">
                {a.photo ? (
                  <img src={a.photo} alt={a.name} className="h-full w-full rounded-full object-cover bg-[#0B1024]" data-testid={`astrologer-photo-${i}`} />
                ) : (
                  <span className="flex h-full w-full items-center justify-center rounded-full bg-[#0E132A] font-serif text-xl text-amber-100" data-testid={`astrologer-initials-${i}`}>{initials(a.name)}</span>
                )}
                <BadgeCheck size={18} className="absolute -bottom-0.5 -right-0.5 text-amber-300 bg-[#0E132A] rounded-full" />
              </div>
              <div className="min-w-0">
                <p className="font-serif text-lg text-slate-50 truncate" data-testid={`astrologer-name-${i}`}>{a.name}</p>
                {a.title && <p className="text-xs text-amber-300/90">{a.title}</p>}
              </div>
            </div>
            {a.bio && <p className="mt-4 text-sm text-slate-400 leading-relaxed">{a.bio}</p>}
            <div className="mt-4 space-y-2 text-sm text-slate-300">
              <p className="flex items-center gap-2" data-testid={`astrologer-exp-${i}`}>
                <Award size={15} className="text-amber-300" />
                {a.experience_years > 0 ? `${a.experience_years}+ years experience` : "Experience: [placeholder]"}
              </p>
              {a.languages?.length > 0 && (
                <p className="flex items-center gap-2" data-testid={`astrologer-langs-${i}`}>
                  <Languages size={15} className="text-amber-300" /> {a.languages.join(", ")}
                </p>
              )}
            </div>
            {a.specialities?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {a.specialities.map((s) => (
                  <span key={s} className="rounded-full border border-amber-500/25 bg-amber-500/5 px-3 py-1 text-[11px] text-amber-200">{s}</span>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
